import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Tabs,
  Tab,
  TextField,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  useTheme,
  alpha,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Card,
  CardContent,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Tag as TagIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../../consts';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PlansListBase from '../Plans/PlansListBase';
import ConfirmationDialog from '../common/ConfirmationDialog';

const ORGANIZATION_CATEGORIES = [
  'Technology', 'Healthcare', 'Finance', 'Education', 'Retail',
  'Manufacturing', 'Consulting', 'Marketing', 'Real Estate', 'Hospitality', 'Other'
];

const OrganizationManagement = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useSelector((state) => state.user);
  const tokenInProcess = useSelector((state) => state.setTokenInProcess);
  const organizationId = userData?.organization_id;

  const [tabValue, setTabValue] = useState(0);

  // Setup state
  const [tenant, setTenant] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [orgName, setOrgName] = useState('');
  const [saving, setSaving] = useState(false);
  const [allowPrivateUse, setAllowPrivateUse] = useState(false);
  const [savingPrivateUse, setSavingPrivateUse] = useState(false);

  // Delete organization state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deletingOrg, setDeletingOrg] = useState(false);

  // Members & invitations state
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [removingMemberId, setRemovingMemberId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', confirmButtonString: 'Confirm', onConfirm: null });

  const [businessPlans, setBusinessPlans] = useState([]);
  const [businessPlansLoading, setBusinessPlansLoading] = useState(false);
  const [businessPlansError, setBusinessPlansError] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // No-org setup state
  const [setupStep, setSetupStep] = useState(1);
  const [setupPurpose, setSetupPurpose] = useState('');
  const [setupOrgData, setSetupOrgData] = useState({
    name: '',
    category: '',
    address: { street: '', city: '', country: '', zipcode: '' }
  });
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError] = useState('');
  const [privateSet, setPrivateSet] = useState(false);

  const fetchTenant = async () => {
    if (!organizationId) return;
    try {
      setTenantLoading(true);
      const response = await axios.get(`${API_URL}/tenants/organizations/${organizationId}`);
      const t = response.data?.tenant || response.data;
      setTenant(t);
      setOrgName(t?.name || '');
      setAllowPrivateUse(Boolean(t?.allow_private_use));
    } catch (error) {
      console.error('Error fetching organization:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to load organization',
        severity: 'error',
      });
    } finally {
      setTenantLoading(false);
    }
  };

  const fetchMembers = async () => {
    if (!organizationId) return;
    try {
      setMembersLoading(true);
      const response = await axios.get(`${API_URL}/tenants/organizations/${organizationId}/members`);
      setMembers(response.data.members || []);
    } catch (error) {
      console.error('Error fetching members:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to load members',
        severity: 'error',
      });
    } finally {
      setMembersLoading(false);
    }
  };

  const fetchInvitations = async () => {
    if (!organizationId) return;
    try {
      setInvitationsLoading(true);
      const response = await axios.get(`${API_URL}/tenants/organizations/${organizationId}/invitations`);
      setInvitations(response.data.invitations || []);
    } catch (error) {
      console.error('Error fetching invitations:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to load invitations',
        severity: 'error',
      });
    } finally {
      setInvitationsLoading(false);
    }
  };

  const fetchMembersAndInvitations = async () => {
    await Promise.all([fetchMembers(), fetchInvitations()]);
  };

  useEffect(() => {
    if (organizationId) {
      fetchTenant();
    } else {
      setTenantLoading(false);
    }
  }, [organizationId]);

  const handleSetupPrivate = async () => {
    setSetupLoading(true);
    setSetupError('');
    try {
      await axios.put(`${API_URL}/profile`, { part_of_organization: false }, { withCredentials: true });
      dispatch({ type: 'SET_USER_DATA', payload: { ...userData, part_of_organization: false } });
      setPrivateSet(true);
    } catch (err) {
      setSetupError(err.response?.data?.error || 'Failed to update. Please try again.');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleSetupOrgFormChange = (field, value) => {
    if (field.startsWith('address.')) {
      const key = field.split('.')[1];
      setSetupOrgData(prev => ({
        ...prev,
        address: { ...prev.address, [key]: value }
      }));
    } else {
      setSetupOrgData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleCreateOrganization = async () => {
    if (!setupOrgData.name.trim()) {
      setSetupError('Organization name is required');
      return;
    }
    if (!setupOrgData.category) {
      setSetupError('Please select a category');
      return;
    }
    setSetupLoading(true);
    setSetupError('');
    try {
      const res = await axios.post(`${API_URL}/tenants/organizations`, {
        name: setupOrgData.name.trim(),
        category: setupOrgData.category,
        address: setupOrgData.address
      }, { withCredentials: true });
      const t = res.data?.tenant || res.data;
      const newOrgId = t?.id || t?.organization_id;
      if (newOrgId) {
        dispatch({
          type: 'SET_USER_DATA',
          payload: { ...userData, organization_id: newOrgId, part_of_organization: true }
        });
        setTenant(t);
        setOrgName(t?.name || setupOrgData.name);
        setSnackbar({ open: true, message: 'Organization created successfully', severity: 'success' });
      }
    } catch (err) {
      setSetupError(err.response?.data?.error || 'Failed to create organization. Please try again.');
    } finally {
      setSetupLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId && tabValue === 1) {
      fetchMembersAndInvitations();
    }
  }, [organizationId, tabValue]);

  const fetchBusinessPlans = async () => {
    try {
      setBusinessPlansLoading(true);
      setBusinessPlansError(null);
      const response = await axios.get(`${API_URL}/plans/business`, { withCredentials: true });
      setBusinessPlans(response.data?.plans || []);
    } catch (error) {
      console.error('Error fetching business plans:', error);
      setBusinessPlansError(error.response?.data?.error || 'Failed to load plans');
      setBusinessPlans([]);
    } finally {
      setBusinessPlansLoading(false);
    }
  };

  useEffect(() => {
    if (tabValue === 2) {
      fetchBusinessPlans();
    }
  }, [tabValue]);

  const handleSaveSetup = async () => {
    if (!organizationId || !orgName.trim()) return;
    try {
      setSaving(true);
      await axios.put(`${API_URL}/tenants/organizations/${organizationId}`, { name: orgName.trim() });
      setTenant((prev) => (prev ? { ...prev, name: orgName.trim() } : null));
      setSnackbar({ open: true, message: 'Organization updated successfully', severity: 'success' });
    } catch (error) {
      console.error('Error updating organization:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to update organization',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAllowPrivateUseChange = async (event) => {
    const checked = event.target.checked;
    if (!organizationId) return;
    try {
      setSavingPrivateUse(true);
      await axios.put(`${API_URL}/tenants/organizations/${organizationId}`, { allow_private_use: checked });
      setTenant((prev) => (prev ? { ...prev, allow_private_use: checked } : null));
      setAllowPrivateUse(checked);
      setSnackbar({
        open: true,
        message: checked ? 'Employees can now share items with the global community.' : 'Private use disabled for employees.',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating allow_private_use:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to update setting',
        severity: 'error',
      });
    } finally {
      setSavingPrivateUse(false);
    }
  };

  const handleDeleteOrganization = async () => {
    if (!organizationId || deleteConfirmName !== (tenant?.name || '')) return;
    try {
      setDeletingOrg(true);
      await axios.post(`${API_URL}/tenants/organizations/${organizationId}/delete`, {
        confirmation_name: deleteConfirmName,
      });
      setSnackbar({ open: true, message: 'Organization deleted successfully', severity: 'success' });
      setOpenDeleteDialog(false);
      setDeleteConfirmName('');
      dispatch({
        type: 'SET_USER_DATA',
        payload: { ...userData, organization_id: null, part_of_organization: false },
      });
      setTenant(null);
      setOrgName('');
    } catch (error) {
      console.error('Error deleting organization:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to delete organization',
        severity: 'error',
      });
    } finally {
      setDeletingOrg(false);
    }
  };

  const handleSendInvitation = async () => {
    if (!inviteEmail.trim() || !organizationId) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      setSnackbar({ open: true, message: 'Please enter a valid email address', severity: 'error' });
      return;
    }
    try {
      setSending(true);
      await axios.post(`${API_URL}/tenants/organizations/${organizationId}/invitations`, {
        email: inviteEmail.trim().toLowerCase(),
      });
      setSnackbar({ open: true, message: 'Invitation sent successfully!', severity: 'success' });
      setInviteEmail('');
      setOpenInviteDialog(false);
      await new Promise(resolve => setTimeout(resolve, 200));
      fetchMembersAndInvitations();
    } catch (error) {
      console.error('Error sending invitation:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to send invitation',
        severity: 'error',
      });
    } finally {
      setSending(false);
    }
  };

  const doRemoveMember = async (memberId) => {
    try {
      setRemovingMemberId(memberId);
      await axios.delete(`${API_URL}/tenants/organizations/${organizationId}/members/${memberId}`);
      setSnackbar({ open: true, message: 'Member removed successfully', severity: 'success' });
      fetchMembersAndInvitations();
    } catch (error) {
      console.error('Error removing member:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to remove member',
        severity: 'error',
      });
    } finally {
      setRemovingMemberId(null);
    }
  };

  const doDeleteInvitation = async (invitationId) => {
    try {
      setDeletingId(invitationId);
      await axios.delete(`${API_URL}/tenants/invitations/${invitationId}`);
      setSnackbar({ open: true, message: 'Invitation revoked successfully', severity: 'success' });
      await new Promise(resolve => setTimeout(resolve, 200));
      fetchMembersAndInvitations();
    } catch (error) {
      console.error('Error deleting invitation:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to revoke invitation',
        severity: 'error',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleRemoveMember = (memberId) => {
    setConfirmDialog({
      open: true,
      title: 'Remove member',
      message: 'Are you sure you want to remove this person from the organization?',
      confirmButtonString: 'Remove',
      onConfirm: () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        doRemoveMember(memberId);
      },
    });
  };

  const handleDeleteInvitation = (invitationId) => {
    setConfirmDialog({
      open: true,
      title: 'Revoke invitation',
      message: 'Are you sure you want to revoke this invitation?',
      confirmButtonString: 'Revoke',
      onConfirm: () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        doDeleteInvitation(invitationId);
      },
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'accepted': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <AccessTimeIcon fontSize="small" />;
      case 'accepted': return <CheckCircleIcon fontSize="small" />;
      case 'rejected': return <CancelIcon fontSize="small" />;
      default: return null;
    }
  };

  if (!userData) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="info">Please log in to manage your organization.</Alert>
      </Container>
    );
  }

  const loadingSkeleton = (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ mb: 4 }}>
        <Skeleton variant="text" width={280} height={40} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={360} height={24} />
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={56} />
        </Grid>
      </Grid>
    </Container>
  );

  // Loading while tenant API is in progress (has org id, fetching organization)
  if (organizationId && tenantLoading) {
    return loadingSkeleton;
  }

  // No organization yet: show setup (create org or work privately), show loading until profile is loaded
  if (!organizationId) {
    if (tokenInProcess) {
      return loadingSkeleton;
    }
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
            }}
          >
            Organization
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Set up your organization or continue as private
          </Typography>
        </Box>

        {privateSet ? (
          <Box sx={{ maxWidth: 560, py: 4 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              You're set up as working privately. You can create an organization anytime below.
            </Alert>
            <Button
              variant="outlined"
              startIcon={<BusinessIcon />}
              onClick={() => { setPrivateSet(false); setSetupStep(2); setSetupPurpose('organizational'); }}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Create organization
            </Button>
          </Box>
        ) : setupStep === 1 ? (
          <Box sx={{ maxWidth: 560 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              For what purpose are you using the system?
            </Typography>
            <RadioGroup
              value={setupPurpose}
              onChange={(e) => {
                const v = e.target.value;
                setSetupPurpose(v);
                if (v === 'private') handleSetupPrivate();
                else setSetupStep(2);
              }}
            >
              <Box
                sx={{
                  mb: 2,
                  p: 3,
                  border: '2px solid',
                  borderColor: setupPurpose === 'private' ? 'primary.main' : 'grey.300',
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main', backgroundColor: alpha(theme.palette.primary.main, 0.05) },
                }}
                onClick={() => { setSetupPurpose('private'); handleSetupPrivate(); }}
              >
                <FormControlLabel
                  value="private"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <PersonIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>Private</Typography>
                        <Typography variant="body2" color="text.secondary">For personal use only</Typography>
                      </Box>
                    </Box>
                  }
                />
              </Box>
              <Box
                sx={{
                  p: 3,
                  border: '2px solid',
                  borderColor: setupPurpose === 'organizational' ? 'primary.main' : 'grey.300',
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main', backgroundColor: alpha(theme.palette.primary.main, 0.05) },
                }}
                onClick={() => { setSetupPurpose('organizational'); setSetupStep(2); }}
              >
                <FormControlLabel
                  value="organizational"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <BusinessIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>Organizational</Typography>
                        <Typography variant="body2" color="text.secondary">For business or team collaboration</Typography>
                      </Box>
                    </Box>
                  }
                />
              </Box>
            </RadioGroup>
          </Box>
        ) : (
          <Box sx={{ maxWidth: 560 }}>
            {setupError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSetupError('')}>{setupError}</Alert>
            )}
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Organization details</Typography>
            <TextField
              fullWidth
              label="Organization name *"
              value={setupOrgData.name}
              onChange={(e) => handleSetupOrgFormChange('name', e.target.value)}
              sx={{ mb: 2 }}
              required
            />
            <FormControl fullWidth sx={{ mb: 2 }} required>
              <InputLabel>Category *</InputLabel>
              <Select
                value={setupOrgData.category}
                onChange={(e) => handleSetupOrgFormChange('category', e.target.value)}
                label="Category *"
              >
                {ORGANIZATION_CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button onClick={() => setSetupStep(1)} disabled={setupLoading}>Back</Button>
              <Button
                variant="contained"
                onClick={handleCreateOrganization}
                disabled={setupLoading || !setupOrgData.name.trim() || !setupOrgData.category}
                startIcon={setupLoading ? <CircularProgress size={20} /> : <BusinessIcon />}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                {setupLoading ? 'Creating...' : 'Create organization'}
              </Button>
            </Box>
          </Box>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    );
  }

  // Profile may use id or user_id; backend owner_user_id is the user's doc id
  const currentUserId = userData?.id ?? userData?.user_id;
  const isOwner = Boolean(tenant && currentUserId && tenant.owner_user_id === currentUserId);
  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
          }}
        >
          Organization
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your organization settings and members
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} aria-label="organization tabs">
          <Tab icon={<SettingsIcon />} iconPosition="start" label="Setup" sx={{ textTransform: 'none', minHeight: 56 }} />
          <Tab icon={<GroupIcon />} iconPosition="start" label="Members" sx={{ textTransform: 'none', minHeight: 56 }} />
          <Tab icon={<StarIcon />} iconPosition="start" label="Plans" sx={{ textTransform: 'none', minHeight: 56 }} />
        </Tabs>
      </Box>

      {/* Setup tab */}
      {tabValue === 0 && (
        <Box>
          {tenantLoading ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
                <Skeleton variant="rounded" height={56} />
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ maxWidth: 560 }}>
              <TextField
                fullWidth
                label="Organization name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                margin="normal"
                variant="outlined"
                disabled={!isOwner}
                helperText={!isOwner ? 'Only the organization owner can change the name.' : undefined}
              />
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, py: 1.5, px: 2, bgcolor: alpha(theme.palette.primary.main, 0.06), borderRadius: 2 }}>
                <TagIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">Shortcut name:</Typography>
                <Typography variant="body1" fontWeight={600}>{tenant?.shortcut_name || '—'}</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                The shortcut name is generated when the organization is created and cannot be changed.
              </Typography>
              {isOwner && (
                <Box sx={{ mt: 3, py: 2, px: 2, bgcolor: alpha(theme.palette.primary.main, 0.04), borderRadius: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={allowPrivateUse}
                        onChange={handleAllowPrivateUseChange}
                        disabled={savingPrivateUse}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight={500}>
                          Allow employees to set private use
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          When enabled, employees can share items with the global community (private use).
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', m: 0 }}
                    labelPlacement="end"
                  />
                </Box>
              )}
              {isOwner && (
                <>
                  <Button
                    variant="contained"
                    onClick={handleSaveSetup}
                    disabled={saving || !orgName.trim() || orgName === tenant?.name}
                    startIcon={saving ? <CircularProgress size={20} /> : null}
                    sx={{ mt: 3, borderRadius: 2, textTransform: 'none', px: 3 }}
                  >
                    {saving ? 'Saving...' : 'Save changes'}
                  </Button>
                  <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Danger zone
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => { setOpenDeleteDialog(true); setDeleteConfirmName(''); }}
                      sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                      Delete organization
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Delete organization confirmation dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => !deletingOrg && setOpenDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { '& .MuiDialog-paper': { borderRadius: 2 } } }}
      >
        <DialogTitle sx={{ color: 'error.main' }}>Delete organization</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This action cannot be undone. All members will be removed from the organization and all invitations will be cancelled.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            To confirm, type the organization name below. Copy-paste is not allowed.
          </Typography>
          <TextField
            fullWidth
            autoComplete="off"
            label="Type organization name"
            placeholder={tenant?.name || ''}
            value={deleteConfirmName}
            onChange={(e) => setDeleteConfirmName(e.target.value)}
            onPaste={(e) => e.preventDefault()}
            onCopy={(e) => e.preventDefault()}
            margin="normal"
            variant="outlined"
            disabled={deletingOrg}
            error={deleteConfirmName.length > 0 && deleteConfirmName !== (tenant?.name || '')}
            helperText={
              deleteConfirmName.length > 0 && deleteConfirmName !== (tenant?.name || '')
                ? 'Name does not match'
                : 'You must type the exact organization name'
            }
            inputProps={{ 'data-lpignore': 'true', 'data-form-type': 'other' }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setOpenDeleteDialog(false)} disabled={deletingOrg}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteOrganization}
            disabled={deletingOrg || deleteConfirmName !== (tenant?.name || '')}
            startIcon={deletingOrg ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {deletingOrg ? 'Deleting...' : 'Delete organization'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Members tab */}
      {tabValue === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setOpenInviteDialog(true)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                boxShadow: 'none',
                '&:hover': { boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}` },
              }}
            >
              Send invitation
            </Button>
          </Box>

          {(membersLoading || invitationsLoading) ? (
            <Grid container spacing={3}>
              {[1, 2, 3].map((i) => (
                <Grid item xs={12} key={i}>
                  <Skeleton variant="rounded" height={48} />
                </Grid>
              ))}
            </Grid>
          ) : members.length === 0 && invitations.filter((i) => i.status === 'pending').length === 0 ? (
            <Fade in timeout={500}>
              <Box
                sx={{
                  textAlign: 'center',
                  py: 8,
                  px: 2,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 3,
                  border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <GroupIcon sx={{ fontSize: 64, color: theme.palette.primary.main, mb: 2, opacity: 0.8 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  No members yet
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', mb: 3 }}>
                  Invite members to join your organization by email.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setOpenInviteDialog(true)}
                  sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
                >
                  Send your first invitation
                </Button>
              </Box>
            </Fade>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Members section */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Members ({members.length})
                </Typography>
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                        <TableCell sx={{ fontWeight: 600 }}>Member</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {members.map((m) => (
                        <TableRow key={m.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PersonIcon fontSize="small" color="action" />
                              <Typography variant="body2">
                                {[m.first_name, m.last_name].filter(Boolean).join(' ') || '—'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{m.email || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            {m.is_owner ? (
                              <Chip label="Owner" color="primary" size="small" />
                            ) : (
                              <Typography variant="body2" color="text.secondary">Member</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {!m.is_owner && isOwner && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveMember(m.id)}
                                disabled={removingMemberId === m.id}
                                title="Remove from organization"
                              >
                                {removingMemberId === m.id ? <CircularProgress size={20} /> : <DeleteIcon fontSize="small" />}
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Invited (pending) section */}
              {invitations.filter((i) => i.status === 'pending').length > 0 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Pending invitations ({invitations.filter((i) => i.status === 'pending').length})
                  </Typography>
                  <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                          <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Sent</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invitations.filter((i) => i.status === 'pending').map((inv) => (
                          <TableRow key={inv.id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EmailIcon fontSize="small" color="action" />
                                <Typography variant="body2">{inv.email}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {inv.created_time ? new Date(inv.created_time).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteInvitation(inv.id)}
                                disabled={deletingId === inv.id}
                                title="Revoke invitation"
                              >
                                {deletingId === inv.id ? <CircularProgress size={20} /> : <DeleteIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}

          <Dialog open={openInviteDialog} onClose={() => setOpenInviteDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonAddIcon color="primary" />
                <Typography variant="h6">Send invitation</Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the email address of the person you want to invite to your organization.
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Email address"
                type="email"
                fullWidth
                variant="outlined"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="user@example.com"
                disabled={sending}
              />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setOpenInviteDialog(false)} disabled={sending}>Cancel</Button>
              <Button
                onClick={handleSendInvitation}
                variant="contained"
                disabled={sending || !inviteEmail.trim()}
                startIcon={sending ? <CircularProgress size={20} /> : <PersonAddIcon />}
              >
                {sending ? 'Sending...' : 'Send invitation'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* Plans tab - same list + subscription as consumer Plans (is_business=true only) */}
      {tabValue === 2 && (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            If you have an active personal subscription, subscribing your organization to a business plan will cancel it and associate your account with the organization's plan.
          </Alert>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Current plan
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your organization is on the {(tenant?.plan || 'free')} plan.
          </Typography>
          <PlansListBase
            plans={businessPlans}
            plansLoading={businessPlansLoading}
            plansError={businessPlansError}
            currentPlanName={tenant?.plan ?? ''}
            planType="business"
            organizationId={organizationId ?? undefined}
            showRedeemCoupon={false}
            showCancelSubscription={false}
            showSandboxBanner={process.env.REACT_APP_PAYPAL_MODE === 'sandbox'}
            title="Business plans"
            subtitle="Select a plan for your organization"
            showDisclaimer={false}
            wrapperClassName="plans-component"
          />
        </Box>
      )}

      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={() => confirmDialog.onConfirm?.()}
        title={confirmDialog.title}
        message={confirmDialog.message}
        cancelButtonString="Cancel"
        confirmButtonString={confirmDialog.confirmButtonString || 'Confirm'}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OrganizationManagement;
